import axios, { type AxiosInstance } from 'axios';
import { api } from 'api';
import { store } from 'store';
import { CLIENT_THEME } from 'utils/clientTheme';
import { hasAccessToMultipleCustomers } from 'utils/validators/role.validation';

export interface MaterialMovementRow {
  excavator: string;
  unloading_area: string;
  trip_count: number;
  total_load_ton: number;
}

export interface CrewHaulageExportPayload {
  customerId: string;
  reportType: 'shift' | 'rotation' | 'engine_hour_summary';
  crewName?: string;
  shiftNumber?: number;
  // Shift-wise report
  shiftDate?: string;
  // Rotation report
  fromDate?: string;
  toDate?: string;
  action: 'download' | 'email' | 'both';
  fileType: 'pdf' | 'xlsx';
  toRecipients?: string[];
  bccRecipients?: string[];
  userRolesToEmail?: string[];
}

export default class ReportAPI {
  private readonly axiosInstance: AxiosInstance;
  private readonly API: ReturnType<typeof api>;

  constructor() {
    const state = store.getState();
    const accessToken = state.authReducer.accessToken;
    const role = state.authReducer.role;
    const customerId = state.persistedReducer.customer.id;

    this.axiosInstance = axios.create({
      baseURL: `${process.env.REACT_APP_MANAGEMENT_API_URL ?? ''}/api/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'x-client-theme': CLIENT_THEME,
      },
      params: {
        ...(role &&
          hasAccessToMultipleCustomers(role) && {
            customerId,
          }),
      },
    });

    this.API = api(this.axiosInstance);
  }

  // Legacy method, kept for backward compatibility
  getReports = async (payload: {
    customerCode: string;
    shiftNumber: number;
    crewName?: string;
    reportMode?: string;
    fileType?: string;
    toRecipients: string[];
    bccRecipients: string[];
    userRolesToEmail: string[];
  }) => {
    const response = await this.API.post('report/crew-haulage/send', payload);
    return response.data;
  };

  exportCrewHaulageFile = async (payload: CrewHaulageExportPayload) => {
    const response = await this.API.post(
      'report/crew-haulage/export',
      payload,
      {
        responseType: 'blob',
      }
    );
    return response.data as Blob;
  };

  // Crew haulage export for email-only actions
  exportCrewHaulageForEmail = async (payload: CrewHaulageExportPayload) => {
    const response = await this.API.post('report/crew-haulage/export', payload);
    return response.data;
  };

  getEquipmentStatusReport = async (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
    shiftStart: string | null;
    shiftEnd: string | null;
  }) => {
    const response = await this.API.post('report/equipment-status', payload);
    return response.data;
  };

  createEquipmentStatusRow = async (payload: {
    customerId: string;
    date: string;
    shift: string;
    equipment?: string | null;
    status?: string | null;
    reason?: string | null;
    location?: string | null;
    region?: string | null;
    comment?: string | null;
    timeCategory?: string | null;
    // ISO 8601 UTC date-time strings
    timeStart?: string | null;
    timeEnd?: string | null;
  }) => {
    const response = await this.API.post(
      'report/equipment-status/row',
      payload
    );
    return response.data;
  };

  updateEquipmentStatusRow = async (payload: {
    id: string;
    customerId: string;
    status?: string | null;
    reason?: string | null;
    location?: string | null;
    region?: string | null;
    comment?: string | null;
    byDispatcher?: boolean | null;
    timeCategory?: string | null;
    // ISO 8601 UTC date-time strings, e.g. "2026-07-27T03:15:00.000Z"
    timeStart?: string | null;
    timeEnd?: string | null;
  }): Promise<{ durationMinutes?: number | null } | undefined> => {
    const { id, ...body } = payload;
    const response = await this.API.patch(
      `report/equipment-status/${id}`,
      body
    );
    return response.data as { durationMinutes?: number | null } | undefined;
  };

  getTripsReport = async (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await this.API.post('report/trips', payload);
    return response.data;
  };

  createTripsReportRow = async (payload: {
    customerId: string;
    date: string;
    shift: string;
    truck?: string | null;
    timeFull?: string | null; // ISO 8601 UTC
    timeEmpty?: string | null; // ISO 8601 UTC
    assignedShovel?: string | null;
    shovel?: string | null;
    material?: string | null;
    blast?: string | null;
    blastType?: string | null;
    blastRegion?: string | null;
    grades?: string | null;
    assignedDump?: string | null;
    dump?: string | null;
    extraLoad?: number | null;
    computedTonnage?: number | null;
    computedVolume?: number | null;
    notes?: string | null;
  }) => {
    const response = await this.API.post('report/trips/row', payload);
    return response.data;
  };

  updateTripsReportRow = async (payload: {
    id: string;
    customerId: string;
    assignedShovel?: string | null;
    assignedDump?: string | null;
    notes?: string | null;
  }) => {
    const { id, ...body } = payload;
    const response = await this.API.patch(`report/trips/${id}`, body);
    return response.data;
  };

  getEquipmentStatusReasons = async (payload: {
    customerId: string;
  }): Promise<
    Array<{
      status: string;
      reasonCategories: string[];
      timeCategories: string[];
    }>
  > => {
    const response = await this.API.get<
      Array<{
        status: string;
        reasonCategories: string[];
        timeCategories: string[];
      }>
    >('report/equipment-status/status-reasons', {
      params: { customerId: payload.customerId },
    });
    return response.data;
  };

  deleteEquipmentStatusRow = async (payload: {
    id: string;
    customerId: string;
  }) => {
    const { id, customerId } = payload;
    const response = await this.API.delete(`report/equipment-status/${id}`, {
      data: { customerId },
    });
    return response.data;
  };

  deleteTripsReportRow = async (payload: {
    id: string;
    customerId: string;
  }) => {
    const { id, customerId } = payload;
    const response = await this.API.delete(`report/trips/${id}`, {
      data: { customerId },
    });
    return response.data;
  };

  getTripReportSensor = async (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await this.API.post('fleet-planning/trip-detail', payload);
    return response.data;
  };

  getMaterialMovementReport = async (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<MaterialMovementRow[]> => {
    const response = await this.API.post(
      'fleet-planning/material-movement-report',
      payload
    );
    return response.data as MaterialMovementRow[];
  };

  getMaterialMovementByDump = async (payload: {
    customerId: string;
    date?: string;
    shift?: string | string[];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<MaterialMovementRow[]> => {
    const response = await this.API.post(
      'fleet-planning/material-movement-by-dump',
      payload
    );
    return response.data as MaterialMovementRow[];
  };

  /**
   * Helper to extract error message from API response, including Blob responses
   */
  static async extractErrorMessage(error: any): Promise<string> {
    let apiMessage: string | undefined;

    // Handle blob error response
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        apiMessage = json.message;
      } catch (parseError) {
        console.error('Failed to parse error blob:', parseError);
      }
    } else {
      apiMessage = error?.response?.data?.message;
    }

    return apiMessage ?? error.message ?? 'An unknown error occurred';
  }
}
